import { useCallback } from "react";
import classNames from "classnames";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import {
  BetaMenuTriggerRoot,
  Button,
  Colors,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  selectedNodeTypeAtom,
  cursorAtom,
  CursorStates,
  selectNodeTypeSetter,
} from "@zapier/canvas-state";

import { PathNodeIcon } from "../icons/PathNodeIcon";
import { GroupIcon } from "../icons/GroupIcon";

import { DefaultNodeIcon } from "./DefaultNodeIcon";
import { MoreMenu } from "./MoreMenu";
import styles from "./DiagrammingToolbar.module.css";

type Props = {
  isDisabled: boolean;
};

export function BasicNodeOptions({ isDisabled }: Props) {
  const selectedNodeType = useAtomValue(selectedNodeTypeAtom);
  const maybeSelectNodeType = useSetAtom(selectNodeTypeSetter);
  const maybeSetCursorMode = useAtomCallback(
    useCallback((get, set, selectedCursorMode: CursorStates) => {
      const cursorMode = get(cursorAtom);
      if (selectedCursorMode === cursorMode) {
        set(cursorAtom, "select");
      } else {
        set(cursorAtom, selectedCursorMode);
      }
    }, []),
  );

  const listItemClass = isDisabled
    ? `${styles.dndnode} ${styles.dndnodeDisabled}`
    : styles.dndnode;

  return (
    <>
      <ul className={styles.horizontalList}>
        <li
          className={classNames(listItemClass, {
            [styles.dndnodeSelected]: selectedNodeType === "default",
          })}
        >
          <Tooltip placement="top" content={isDisabled ? "" : "Default step"}>
            <TooltipFocusableTrigger>
              <button
                aria-label="Add a default step"
                className={styles.dndButton}
                disabled={isDisabled}
                onClick={() => {
                  maybeSelectNodeType("default");
                }}
                onKeyDown={() => {
                  maybeSelectNodeType("default");
                }}
              >
                <DefaultNodeIcon
                  color={
                    selectedNodeType === "default"
                      ? Colors.UiPrimaryStrongest
                      : Colors.GrayWarm10
                  }
                />
              </button>
            </TooltipFocusableTrigger>
          </Tooltip>
        </li>
        <li
          className={classNames(listItemClass, {
            [styles.dndnodeSelected]: selectedNodeType === "path",
          })}
        >
          <Tooltip placement="top" content={isDisabled ? "" : "Split path"}>
            <TooltipFocusableTrigger>
              <button
                aria-label="Add a path step"
                className={styles.dndButton}
                disabled={isDisabled}
                onClick={() => {
                  maybeSelectNodeType("path");
                }}
                onKeyDown={() => {
                  maybeSelectNodeType("path");
                }}
              >
                <PathNodeIcon />
              </button>
            </TooltipFocusableTrigger>
          </Tooltip>
        </li>
        <li
          className={classNames(listItemClass, {
            [styles.dndnodeSelected]: selectedNodeType === "text",
          })}
        >
          <Tooltip placement="top" content={isDisabled ? "" : "Text"}>
            <TooltipFocusableTrigger>
              <button
                aria-label="Add text"
                className={styles.dndButton}
                disabled={isDisabled}
                onClick={() => {
                  maybeSelectNodeType("text");
                }}
                onKeyDown={() => {
                  maybeSelectNodeType("text");
                }}
              >
                <Icon
                  name="formText"
                  color={
                    selectedNodeType === "text"
                      ? "UiPrimaryStrongest"
                      : "GrayWarm10"
                  }
                  size={30}
                />
              </button>
            </TooltipFocusableTrigger>
          </Tooltip>
        </li>
        <li
          className={classNames(listItemClass, {
            [styles.dndnodeSelected]: selectedNodeType === "group",
          })}
        >
          <Tooltip placement="top" content={isDisabled ? "" : "Group"}>
            <TooltipFocusableTrigger>
              <button
                aria-label="Add group"
                className={styles.dndButton}
                disabled={isDisabled}
                onClick={() => {
                  maybeSelectNodeType("group");
                  maybeSetCursorMode("add-group");
                }}
                onKeyDown={() => {
                  maybeSelectNodeType("group");
                  maybeSetCursorMode("add-group");
                }}
              >
                <GroupIcon isSelected={selectedNodeType === "group"} />
              </button>
            </TooltipFocusableTrigger>
          </Tooltip>
        </li>
        <li className={styles.moreButtonWrapper}>
          <BetaMenuTriggerRoot>
            <Button
              variant="ghost"
              aria-label="More"
              iconPosition="start"
              isDisabled={isDisabled}
            >
              <Icon name="arrowSmallDown" />
            </Button>
            <MoreMenu />
          </BetaMenuTriggerRoot>
        </li>
      </ul>
    </>
  );
}
